import {
  PutObjectCommand,
  S3Client,
  S3ClientConfig,
  PutObjectCommandInput,
  ListObjectsV2Command,
  GetObjectCommand,
} from "@aws-sdk/client-s3";

import { S3_PUSH_TO_TALK_BUCKET } from "./constants";
import { PttOriginType, PttTarget, PttTargetType } from "../types";
import { sendPttRequest } from "./api";
import moment from "../../../utils/moment";

import type { Window as WindowType } from "@/types/window";
const store = (window as WindowType).store;

// This needs to be a function to get the credentials from the store
// Otherwise, the credentials are not updated when the user changes
export const s3 = async (): Promise<S3Client> => {
  const storage = await store.getters["operators/secret"]("storage");
  if (!storage?.key) {
    alert("Es necesario cerrar sesión y volver a entrar de la aplicación.");
    throw new Error("No credentials found");
  }
  return new S3Client({
    region: "eu-west-1",
    credentials: {
      accessKeyId: storage.key,
      secretAccessKey: storage.secret,
    },
  } as S3ClientConfig);
};

const urlEncode = (str: string): string => {
  return encodeURIComponent(str)?.replace(/%20/g, "+");
};

const urlDecode = (str: string): string => {
  return decodeURIComponent(str?.replace(/\+/g, "%20"));
};

const resolveCentralName = (wgId: number, centralId: number): string => {
  const central = store.getters["app/centralsByWorkgroupId"](wgId).find(
    (c: { id: number; name: string }) => c.id === centralId
  );
  return central?.name ?? centralId;
};

const getIdFromLicense = (license: string): number => {
  const taxi = store.getters["app/taxis"].find(
    (t: { id: number; license: string }) => t.license === license
  );
  return taxi?.id ?? 0;
};

export async function uploadPushToTalk(
  wgId: number,
  central: number,
  target: PttTarget,
  data: Buffer | Blob
): Promise<void> {
  const operator = store.getters["operators/operator"];
  const year = moment().utc().format("YYYY");
  const month = moment().utc().format("MM");
  const day = moment().utc().format("DD");

  const pathParts = [
    "wgs",
    wgId,
    "centrals",
    central,
    year,
    month,
    day,
    PttOriginType.AGENT,
    `${operator.id}_${urlEncode(operator.name)}`,
    target.type,
  ];
  if (target.type === PttTargetType.TAXI) {
    const taxi = store.getters["app/taxis"].find(
      (t: { id: number; license: string }) => t.id === target.id
    );
    pathParts.push(`${urlEncode(taxi?.license ?? "NONE")}`);
  }
  const path = pathParts.join("/"); // wgs/1/centrals/1/2024/01/01/agent/{agentId}_{agentName}/taxi/{license}
  const datetime = formatDateTimeForFilename();
  const filename = `${datetime}_${crypto.randomUUID()}.m4a`;

  await (
    await s3()
  ).send(
    new PutObjectCommand({
      Key: `${path}/${filename}`,
      Bucket: S3_PUSH_TO_TALK_BUCKET,
      Body: data,
      ContentType: "audio/mp4",
    } as PutObjectCommandInput)
  );

  await sendPttRequest({
    target: target.type === PttTargetType.TAXI ? 3 : 2,
    wgId: wgId,
    targetParams: target.type === PttTargetType.TAXI ? [target.id!] : [],
    url: `${path}/${filename}`,
  });
}

export async function downloadPushToTalk(url: string): Promise<Blob> {
  const fileExtension = url.split(".").pop()?.toLowerCase();
  const contentType = fileExtension === "m4a" ? "audio/mp4" : "audio/mp3";

  const response = await (
    await s3()
  ).send(
    new GetObjectCommand({
      Bucket: S3_PUSH_TO_TALK_BUCKET,
      Key: url,
    })
  );

  if (!response.Body) {
    throw new Error("No audio data received from S3");
  }

  const byteArray = await response.Body.transformToByteArray();
  return new Blob([byteArray], { type: contentType });
}

export type PttItem = {
  key: string;
  wg: number;
  central: number;
  date: Date;
  title: string;
  origin: string;
  destination: string;
  metadata: {
    central: string;
    origin: string;
    originid: number;
    originresolved: string;
    destination: string;
    destinationid?: number;
    destinationresolved?: string;
  };
};

export async function listPushToTalk(
  wgIds: string | string[],
  centrals: string | string[],
  from: Date
): Promise<PttItem[]> {
  const current = moment()(from).utc();
  const wgIdArray = Array.isArray(wgIds) ? wgIds : [wgIds];
  const centralArray = Array.isArray(centrals) ? centrals : [centrals];

  const allFiles: string[] = [];

  while (current.isBefore(moment()().utc().startOf("day").add(1, "day"))) {
    const year = current.format("YYYY");
    const month = current.format("MM");
    const day = current.format("DD");

    // For each workgroup and central combination, we need to search
    for (const wgId of wgIdArray) {
      for (const central of centralArray) {
        if (!central) continue;
        const command = new ListObjectsV2Command({
          Bucket: S3_PUSH_TO_TALK_BUCKET,
          Prefix: `wgs/${wgId}/centrals/${central}/${year}/${month}/${day}`,
          MaxKeys: 10000,
        });

        const response = await (await s3()).send(command);

        const files = response.Contents?.map((content) => content.Key!) ?? [];
        const filteredFiles = files.filter((file) => {
          const fileDate = dateFromFilename(file.split("/").pop()!);
          if (fileDate < from) {
            return false;
          }
          return true;
        });

        allFiles.push(...filteredFiles);
      }
    }
    current.add(1, "day");
  }

  const results = await Promise.all(
    allFiles.map(async (file) => {
      return parsePttFile(file);
    })
  );

  return results.sort((a, b) => b.date.getTime() - a.date.getTime());
}

export function parsePttFile(file: string): PttItem {
  const date = dateFromFilename(file.split("/").pop()!);
  const wg = parseInt(file.split("/")[1]);
  const central = parseInt(file.split("/")[3]);
  const origin = file.split("/")[7];
  const originid =
    origin === "taxi"
      ? getIdFromLicense(file.split("/")[8])
      : parseInt(file.split("/")[8].split("_")[0]);
  const originresolved =
    origin === "taxi" ? file.split("/")[8] : urlDecode(file.split("/")[8].split("_")[1]);
  const destination = file.split("/")[9];
  const destinationid = destination === "taxi" ? getIdFromLicense(file.split("/")[10]) : undefined;
  const destinationresolved = file.split("/")[10];

  let originString = "";
  switch (origin) {
    case "agent":
      originString = `Agente ${originresolved}`;
      break;
    case "taxi":
      originString = `Taxi ${originresolved}`;
      break;
    default:
      originString = "-";
  }

  let destinationString = "";
  switch (destination) {
    case "fleet":
      destinationString = "Flota";
      break;
    case "central":
      destinationString = `Central`;
      break;
    case "taxi":
      destinationString = `Taxi ${destinationresolved}`;
      break;
    default:
      destinationString = "-";
  }

  const title = `PTT de ${originString} a ${destinationString}`;

  return {
    key: file,
    wg,
    central,
    date,
    title,
    origin: originString,
    destination: destinationString,
    metadata: {
      central: resolveCentralName(wg, central),
      origin,
      originid,
      originresolved,
      destination,
      destinationid,
      destinationresolved,
    },
  };
}

function formatDateTimeForFilename(date: Date = new Date()): string {
  return moment()(date).format("YYYYMMDDTHHmmss");
}

function dateFromFilename(filename: string): Date {
  return moment()(filename.split("_")[0], "YYYYMMDDTHHmmss").toDate();
}
